/*  UI  */
import {
    Badge,
    Button,
    Card,
    Flex,
    IconWithVariant
} from '@auviknetworks/iceberg-ui';
import { Editor } from '@monaco-editor/react';
import {
    Alert,
    Skeleton,
    Typography,
    TypographyProps,
    useTheme
} from '@mui/material';

/* Shared */
import { Permission } from 'application/Shared/Components/Core';
import { TooltipPlacement } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import { Dropdown } from 'application/Shared/Dropdown';

/* Hooks */
import { useUserTheme } from 'hooks/dataAccess/User/userTheme';

/*  React  */
import MuiMarkdown from 'mui-markdown';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';

/* Internal */
import { TruncatedText } from '../../TruncatedText';
import { useDashboardContext } from '../DashboardContext';
import { WithFullScreen } from '../Helpers/WithFullScreen';
import { templateInterpolation } from '../Helpers/templateInterpolation';
import { type Panel as PanelType, Variables } from '../types';
import {
    PanelContextProvider,
    PanelMode,
    type PanelState,
    usePanelContext
} from './PanelContext';
import { PanelFooter } from './PanelFooter';

interface PanelProps extends Partial<PanelState> {
    /** The children of the panel. */
    children?: React.ReactNode;
    /** The props for the panel title. */
    titleProps?: TypographyProps;
    /** The skeleton for the panel. This is used to show a loading state for the panel. */
    skeleton?: React.ReactNode;
}

const JsonView = ({ data }: { data: unknown }) => {
    const userTheme = useUserTheme();

    return (
        <Flex flex={1} flexDirection="column">
            <Editor
                wrapperProps={{
                    style: {
                        display: 'flex',
                        flex: 3,
                        height: '160px',
                        padding: '0',
                        position: 'relative',
                        textAlign: 'initial',
                        width: '100%'
                    }
                }}
                height="160px"
                defaultLanguage="json"
                theme={userTheme === 'dark' ? 'vs-dark' : 'vs-light'}
                value={JSON.stringify(data, null, 2)}
                options={{
                    minimap: { enabled: false },
                    readOnly: true,
                    scrollBeyondLastLine: false
                }}
            />
        </Flex>
    );
};

const PanelMenu = () => {
    const { t } = useTranslation();
    const {
        canShowCompactView,
        dashboardState: { isEditing },
        setIsCompactView
    } = useDashboardContext();
    const {
        queryResults: { refetch, isFetching },
        panelState: { canEdit, canFetch, canFullScreen, isFullScreen, mode },
        schema: { id, title },
        setIsFullScreen,
        setMode
    } = usePanelContext();

    return (
        <Flex
            className="settings"
            flexDirection="row"
            {...(title ? {} : { flex: 1, justifyContent: 'flex-end' })}
            sx={{
                flexDirection: 'row',
                gap: '8px',
                justifyContent: 'flex-end',
                margin: '24px',
                marginRight: canEdit ? '0px' : '24px',
                minHeight: '32px',
                visibility: 'hidden'
            }}
        >
            {canFetch && (
                <Permission required="super_admin">
                    <Button
                        data-trackid="dashboard_panels_refresh"
                        title={t('Dashboard.Panel.refresh')}
                        onClick={refetch}
                        size="icon"
                        textStyle="greyIcon"
                        variant="icon"
                        // Icon button is slightly bigger than the other buttons, so we need to set the height and width to 32px
                        sx={{ height: '32px', width: '32px' }}
                    >
                        <IconWithVariant
                            src="Refresh"
                            size="16px"
                            wrapperProps={{
                                animation: isFetching
                                    ? 'spinPause 1.5s linear infinite'
                                    : undefined
                            }}
                        />
                    </Button>
                </Permission>
            )}
            {canFullScreen && (
                <Button
                    data-trackid={`fullScreen_${
                        isFullScreen ? 'collapse' : 'expand'
                    }_${id}`}
                    title={t(
                        isFullScreen
                            ? 'Dashboard.Panel.fullScreen.collapse'
                            : 'Dashboard.Panel.fullScreen.expand'
                    )}
                    onClick={() => setIsFullScreen((v: boolean) => !v)}
                    size="icon"
                    textStyle="greyIcon"
                    // Icon button is slightly bigger than the other buttons, so we need to set the height and width to 32px
                    sx={{ height: '32px', width: '32px' }}
                    variant="icon"
                >
                    <IconWithVariant
                        src={isFullScreen ? 'Collapse' : 'Expand'}
                        size="16px"
                    />
                </Button>
            )}
            {canShowCompactView && (
                <Flex
                    sx={{
                        alignItems: 'baseline',
                        flexWrap: 'wrap',
                        gap: '8px',
                        visibility: 'visible'
                    }}
                >
                    <Button
                        data-trackid="dashboard_workspace_showNormalView"
                        variant="icon"
                        size="icon"
                        disabled={isEditing}
                        title={t('Dashboard.filters')}
                        textStyle="greyIcon"
                        // Icon button is slightly bigger than the other buttons, so we need to set the height and width to 32px
                        sx={{ height: '32px', width: '32px' }}
                        onClick={() => setIsCompactView(false)}
                    >
                        <IconWithVariant src="Apps" size={FontSizes[2]} />
                    </Button>
                    <Button
                        data-trackid="dashboard_workspace_showCompactView"
                        variant="icon"
                        size="icon"
                        disabled={isEditing}
                        title={t('Dashboard.filters')}
                        textStyle="greyIcon"
                        // Icon button is slightly bigger than the other buttons, so we need to set the height and width to 32px
                        sx={{ height: '32px', width: '32px' }}
                        onClick={() => setIsCompactView(true)}
                    >
                        <IconWithVariant src="List" size={FontSizes[2]} />
                    </Button>
                </Flex>
            )}
            {canEdit && (
                <Permission required="super_admin">
                    <Dropdown
                        menu={[
                            {
                                action: () => setMode(PanelMode.NORMAL),
                                labelKey: 'Common.normal',
                                prefix:
                                    mode === PanelMode.NORMAL
                                        ? 'Check'
                                        : undefined,
                                type: 'leaf'
                            },
                            {
                                action: () => setMode(PanelMode.SCHEMA),
                                labelKey: 'Common.schema',
                                prefix:
                                    mode === PanelMode.SCHEMA
                                        ? 'Check'
                                        : undefined,
                                type: 'leaf'
                            },
                            {
                                action: () => setMode(PanelMode.DATA),
                                labelKey: 'Common.data',
                                prefix:
                                    mode === PanelMode.DATA
                                        ? 'Check'
                                        : undefined,
                                type: 'leaf'
                            },
                            {
                                action: () => setMode(PanelMode.LOADING),
                                labelKey: 'Common.loading',
                                prefix:
                                    mode === PanelMode.LOADING
                                        ? 'Check'
                                        : undefined,
                                type: 'leaf'
                            },
                            {
                                action: () => setMode(PanelMode.NO_DATA),
                                labelKey: 'Common.noData',
                                prefix:
                                    mode === PanelMode.NO_DATA
                                        ? 'Check'
                                        : undefined,
                                type: 'leaf'
                            }
                        ]}
                    >
                        <Button
                            data-trackid="dashboard_panels_settingsMenu"
                            title={t('Dashboard.Panel.settings')}
                            size="icon"
                            textStyle="greyIcon"
                            variant="iconWithoutBorder"
                        >
                            <IconWithVariant src="Menu" size="14px" />
                        </Button>
                    </Dropdown>
                </Permission>
            )}
        </Flex>
    );
};

/**
 * Skeleton for the panel.
 * This is used to show a loading state for the panel.
 */
const DefaultSkeleton = () => (
    <Flex padding="0 24px" flexDirection="column">
        <Skeleton variant="text" width="100%" height="48px" />
        <Skeleton variant="text" width="60%" height="48px" />
        <Skeleton variant="text" width="60%" height="48px" />
    </Flex>
);

/**
 * Basic Panel component using IcebergUI Card
 */
export const PanelComponent = ({
    children,
    titleProps,
    skeleton = <DefaultSkeleton />
}: PanelProps) => {
    const { t } = useTranslation();
    const { palette, components } = useTheme();
    const {
        dashboardState: { isEditing: isDashboardEditing },
        format
    } = useDashboardContext();
    const {
        queryResults: { isLoading, isError, data },
        panelState,
        schema: panel,
        ref,
        context: panelContext,
        setIsFullScreen,
        setHeight
    } = usePanelContext();

    const { isFullScreen, height, mode, noData } = panelState;

    useEffect(() => {
        const newHeight = ref.current?.clientHeight;
        if (newHeight && newHeight !== height) {
            setHeight(ref.current.clientHeight);
        }
        // Adding ref.current?.clientHeight to ensure the item inside the panel re-renders with the right dimension if ref.current?.clientHeight changes
        // ref does not change on window resize, even though its clientHeight changes.
    }, [height, ref, ref.current?.clientHeight, setHeight]);

    // Set the title size to medium if not specified in the panel format.
    const { titleSize = 'medium' } = panel.format ?? {};

    const [searchParams, setSearchParams] = useSearchParams();

    useEffect(() => {
        // Check URL on first render and expand panel if ID matches
        const fullScreenPanelId = searchParams.get('fullScreen');
        if (fullScreenPanelId === panel.id) {
            setIsFullScreen(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps -- Empty deps array for first render only
    }, []);

    useEffect(() => {
        // Add or remove fullScreen param based on isFullScreen state
        if (isFullScreen) {
            searchParams.set('fullScreen', panel.id);
        } else if (searchParams.get('fullScreen') === panel.id) {
            searchParams.delete('fullScreen');
        }
        // We don't want the layout changes in history
        setSearchParams(searchParams, { replace: true });
    }, [isFullScreen, panel.id, searchParams, setSearchParams]);

    const { description: showDescription = true } = panel.meta ?? {};
    const { tags: showTags = false } = panel.format ?? {};

    return (
        <WithFullScreen
            isFullScreen={isFullScreen}
            setIsFullScreen={setIsFullScreen}
        >
            <Card
                data-testid="dashboardPanelCard"
                sx={{
                    // show settings block only when hovering over the card
                    '&:hover > div > .settings': {
                        visibility: 'visible'
                    },
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%',
                    minHeight: 0,
                    minWidth: '200px',
                    paddingBottom: '24px',
                    width: '100%'
                }}
                paddingBottom="24px"
            >
                <Flex
                    data-testid="dashboardPanelTitle"
                    sx={{
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        minHeight: 0,
                        width: '100%'
                    }}
                >
                    {panel.title && (
                        <Flex sx={{ flex: 1, justifyContent: 'flex-start' }}>
                            <Typography
                                // Required for the dashboard library to properly position the panel
                                className="dashboard-panel-title"
                                padding="24px"
                                component="h2"
                                sx={{
                                    cursor: isDashboardEditing
                                        ? 'move'
                                        : 'default',
                                    ...components?.Panel.title[titleSize]
                                }}
                                {...titleProps}
                            >
                                {templateInterpolation(t(panel.title), {
                                    ...panelContext.variables,
                                    panelState:
                                        panelState as unknown as Variables
                                })}
                            </Typography>
                        </Flex>
                    )}
                    <PanelMenu />
                </Flex>
                {showDescription && panel.description && (
                    <Flex
                        data-testid="dashboardPanelDescription"
                        sx={{ minHeight: 0, padding: '0 24px 16px' }}
                    >
                        <TruncatedText
                            maxLines={3}
                            text={
                                <MuiMarkdown>
                                    {templateInterpolation(
                                        t(panel.description),
                                        {
                                            ...panelContext.variables,
                                            panelState:
                                                panelState as unknown as Variables
                                        }
                                    )}
                                </MuiMarkdown>
                            }
                            tooltipPlacement={TooltipPlacement.BOTTOM}
                        />
                    </Flex>
                )}
                {showTags && panel.tags && (
                    <Flex
                        data-testid="dashboardPanelTags"
                        sx={{
                            flexWrap: 'wrap',
                            gap: '4px',
                            minHeight: 0,
                            padding: '0 20px' // 20px to account for iceberg's badge margin of 4px
                        }}
                    >
                        {panel.tags.map((tag) => (
                            <Badge
                                key={tag}
                                variant={format?.tagVariants?.[tag]}
                            >
                                {t(`Tags.${tag}`)}
                            </Badge>
                        ))}
                    </Flex>
                )}
                <Flex
                    data-testid="dashboardPanelMainBody"
                    ref={ref}
                    sx={{ flex: 1, flexDirection: 'column', minHeight: 0 }}
                >
                    {(isLoading || mode === PanelMode.LOADING) && skeleton}
                    {isError && (
                        <Flex
                            sx={{
                                margin: 'auto 0',
                                minHeight: 0,
                                padding: '0 24px',
                                width: '100%'
                            }}
                        >
                            <Alert severity="error">{t('Common.error')}</Alert>
                        </Flex>
                    )}
                    {!isLoading && !noData && mode === PanelMode.NORMAL && (
                        <Flex
                            data-testid="dashboardPanelMainBodyContent"
                            sx={{
                                flex: 1,
                                flexDirection: 'column',
                                height: '100%',
                                minHeight: 0
                            }}
                        >
                            {children}
                        </Flex>
                    )}
                    {!isLoading &&
                        ((noData && mode === PanelMode.NORMAL) ||
                            mode === PanelMode.NO_DATA) && (
                            <Flex
                                sx={{
                                    justifyContent: 'center',
                                    margin: 'auto 0',
                                    padding: '0 24px',
                                    width: '100%'
                                }}
                            >
                                <Typography
                                    component="span"
                                    color={palette.text.noData}
                                    sx={{
                                        fontSize: FontSizes[2]
                                    }}
                                >
                                    {t('Common.noData')}
                                </Typography>
                            </Flex>
                        )}
                    {(mode === PanelMode.DATA || mode === PanelMode.SCHEMA) && (
                        <JsonView
                            data={mode === PanelMode.DATA ? data : panel}
                        />
                    )}
                </Flex>
                {panel.footer && <PanelFooter />}
            </Card>
        </WithFullScreen>
    );
};

/**
 * Panel component wrapper
 * Wraps the PanelComponent with the PanelContextProvider and provides data context.
 */
export const Panel = <TPanel extends PanelType>({
    children,
    panel,
    titleProps,
    canFullScreen = true,
    canEdit = true,
    canFetch = true,
    skeleton
}: PanelProps & {
    panel: TPanel;
}) => (
    <PanelContextProvider<TPanel>
        canEdit={canEdit}
        canFetch={canFetch && panel?.data?.source === 'rest'}
        canFullScreen={canFullScreen}
        schema={panel}
    >
        <PanelComponent titleProps={titleProps} skeleton={skeleton}>
            {children}
        </PanelComponent>
    </PanelContextProvider>
);
